import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Store, ArrowRight, X, Sparkles } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const DISMISS_KEY = 'gizi_seller_cta_dismissed';

export const SellerCTABanner = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [isDismissed, setIsDismissed] = useState(true);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (!dismissed) {
      setIsDismissed(false);
      // Delay animation entrance
      const timer = setTimeout(() => setIsVisible(true), 300);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleDismiss = (e) => {
    e.stopPropagation();
    setIsVisible(false);
    setTimeout(() => {
      setIsDismissed(true);
      localStorage.setItem(DISMISS_KEY, 'true');
    }, 400);
  };

  const handleClick = () => {
    navigate('/seller-onboarding');
  };

  if (isDismissed) return null;

  return (
    <div
      className={`
        relative overflow-hidden cursor-pointer
        transition-all duration-500 ease-out
        ${isVisible
          ? 'opacity-100 translate-y-0 max-h-[500px]'
          : 'opacity-0 translate-y-4 max-h-0'
        }
      `}
      onClick={handleClick}
      data-testid="seller-cta-banner"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
        <div className="relative rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
          {/* Background gradient */}
          <div
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(135deg, #FF9900 0%, #FFB84D 30%, #FF8C00 70%, #E67300 100%)',
            }}
          />

          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-10 -translate-y-1/2 translate-x-1/4"
            style={{ background: 'radial-gradient(circle, white 0%, transparent 70%)' }}
          />
          <div className="absolute bottom-0 left-1/3 w-48 h-48 rounded-full opacity-10 translate-y-1/2"
            style={{ background: 'radial-gradient(circle, white 0%, transparent 70%)' }}
          />

          {/* Animated grain texture */}
          <div className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E")`,
            }}
          />

          {/* Content */}
          <div className="relative z-10 flex items-center justify-between px-6 md:px-10 py-6 md:py-8">
            {/* Left: icon + text */}
            <div className="flex items-center gap-4 md:gap-6 flex-1 min-w-0">
              {/* Icon container */}
              <div className="flex-shrink-0 w-14 h-14 md:w-16 md:h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/30">
                <Store className="w-7 h-7 md:w-8 md:h-8 text-white" />
              </div>

              {/* Text */}
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className="w-4 h-4 text-yellow-200 flex-shrink-0" />
                  <span className="text-xs md:text-sm font-semibold text-yellow-100 uppercase tracking-wider">
                    {t('seller_cta_badge') || 'Peluang Bisnis'}
                  </span>
                </div>
                <h3 className="font-heading text-lg md:text-2xl font-bold text-white leading-tight">
                  {t('seller_cta_title') || 'Mulai Berjualan di GIZI.AI'}
                </h3>
                <p className="text-sm md:text-base text-white/80 mt-1 hidden sm:block">
                  {t('seller_cta_subtitle') || 'Jangkau lebih banyak pelanggan dengan bergabung sebagai mitra GIZI.AI'}
                </p>
              </div>
            </div>

            {/* Right: CTA button + close */}
            <div className="flex items-center gap-3 flex-shrink-0 ml-4">
              <button
                className="hidden md:flex items-center gap-2 bg-white text-[#E67300] px-6 py-2.5 rounded-full font-bold text-sm hover:bg-yellow-50 active:scale-95 transition-all shadow-md hover:shadow-lg"
                onClick={handleClick}
              >
                {t('seller_cta_button') || 'Pelajari Caranya'}
                <ArrowRight className="w-4 h-4" />
              </button>

              {/* Mobile arrow */}
              <div className="md:hidden flex items-center justify-center w-10 h-10 bg-white/20 rounded-full">
                <ArrowRight className="w-5 h-5 text-white" />
              </div>

              {/* Dismiss button */}
              <button
                className="flex items-center justify-center w-8 h-8 rounded-full bg-black/10 hover:bg-black/20 transition-colors"
                onClick={handleDismiss}
                aria-label="Tutup banner"
                data-testid="seller-cta-dismiss"
              >
                <X className="w-4 h-4 text-white/80" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SellerCTABanner;
